const { gql } = require('graphql-tag');
const utilities = require('../utilities');

('use strict');

var _parent = {};
let log = true;

var locations = {
  init: p => (_parent = p),
  /**
   * Fetches a matching location object, returning it in an array inside of a response object.
   *
   * @param  {BigInteger} id - The ID of a location.
   * @return  {Object} The response object containing the matching location.
   */
  getById: id =>
    _parent.gql.query(
      gql`
        query getLocationById($id: bigint!) {
          locations(where: { id: { _eq: $id } }) {
            ...Location
          }
        }
        ${_parent.fragments.location()}
      `,
      { id: id }
    ),
  /**
   * Fetches all location objects that match the entered variables, returning them in an array inside of a respone object.
   *
   * @param  {Object} [variables = null] - Variables used in where clause;
   * e.g.: {region_id: 2, active: 1} to return all active locations for region 2.
   * A null value (default) will return all locations the client has permissions for.
   * @return  {Object} The response object containing all matching locations.
   */
  get: (variables = null) =>
    _parent.gql.query(
      gql`
        query getLocations${_parent.gql.buildVariablesDeclarationString('locations', variables)} {
          locations${_parent.gql.buildWhereClauseString(variables)} {
            ...Location
          }
        }
        ${_parent.fragments.location()}
      `
    ),
  /**
   * Inserts a new location object with the entered values, then returns a response object containing the new location.
   *
   * @param  {Object} location  The location object to create
   * @param  {number} location.customer_id  The ID of the related customer (must be an integer)
   * @param  {number} location.region_id  The ID of the related region (must be an integer)
   * @param  {string} [location.place_id]  The Google Place ID of the location (returned from Google Places API lookup)
   * @param  {("customer"|"consumer residential"|"consumer business")} [location.type]  The location type (default is 'customer')
   * @param  {string} [location.name]  The name of the location (generally returned from Google Places API lookup, or set by customer)
   * @param  {string} [location.nickname]  Optional nickname set by customer
   * @param  {string} [location.address]  The full address of the location (generally returned from Google Places API)
   * @param  {string} [location.address_line_one]  The street address of the location (generally returned from Google Places API)
   * @param  {string} [location.address_line_two]  The Apartment/PO box address of the location (generally returned from Google Places API)
   * @param  {string} [location.city]  The city of the location (generally returned from Google Places API)
   * @param  {string} [location.state]  The state of the location (generally returned from Google Places API)
   * @param  {string} [location.zip_code]  The zip code of the location (generally returned from Google Places API)
   * @param  {number} location.latitude  The latitude of the location (generally returned from Google Places API)
   * @param  {number} location.longitude  The longitude of the location (generally returned from Google Places API)
   * @param  {string} [location.email]  Optional contact email address
   * @param  {string} [location.phone]  Optional contact phone number
   * @return  {Object}  The response object containing the newly created location object.
   */
  create: location =>
    _parent.gql.mutation(
      gql`
        mutation insert_location(
          $address: String!
          $address_line_one: String
          $address_line_two: String
          $city: String
          $state: String
          $zip_code: String
          $customer_id: bigint!
          $email: String
          $latitude: numeric!
          $longitude: numeric!
          $name: String!
          $nickname: String
          $phone: String
          $place_id: String
          $region_id: bigint
          $timezone: String
          $type: String!
        ) {
          insert_locations(
            on_conflict: { constraint: locations_customer_id_address_key, update_columns: [active] }
            objects: {
              active: 1
              address: $address
              address_line_one: $address_line_one
              address_line_two: $address_line_two
              city: $city
              state: $state
              zip_code: $zip_code
              customer_id: $customer_id
              email: $email
              latitude: $latitude
              longitude: $longitude
              name: $name
              nickname: $nickname
              phone: $phone
              place_id: $place_id
              region_id: $region_id
              timezone: $timezone
              type: $type
            }
          ) {
            affected_rows
            returning {
              ...Location
            }
          }
        }
        ${_parent.fragments.location()}
      `,
      location
    ),
  /**
   * Updates a location object with matching ID, then returns a response object with the GQL response.
   *
   * @param  {BigInteger} id - The ID of a location.
   * @return  {Object} The response object confirming the update.
   */
  updateById: (id, variables) =>
    _parent.gql.mutation(
      gql`
        mutation updateLocationById($id: bigint!) {
          update_locations(
            where: {id: {_eq: $id}},
            _set: ${_parent.gql.buildUpdateClauseString(variables)}
            ) {
            affected_rows
            returning {
              ...Location
              
            }
          }
        }   
        ${_parent.fragments.location()}   
      `,
      { id: id }
    ),
  /**
   * Deletes a location object with matching ID, then returns a response object with the GQL response.
   *
   * @param  {BigInteger} id - The ID of a location.
   * @return  {Object} The response object confirming the deletion.
   */
  deleteById: id =>
    _parent.gql.mutation(
      gql`
        mutation deleteLocationById($id: bigint!) {
          delete_locations(where: { id: { _eq: $id } }) {
            affected_rows
            returning {
              ...Location
            }
          }
        }
        ${_parent.fragments.location()}
      `,
      { id: id }
    ),

  /**
   * Query a location by address or place_id to check that it exists
   *
   * @param {bigint} customer_id - ID of the customer associated with the location
   * @param {String} address - the address of the location (optional)
   * @param {String} place_id - Google place_id of the location (optional)
   */
  getByAddressOrPlaceId: (customer_id, address = '', place_id = '') =>
    _parent.gql.query(
      gql`
        query get_locations_by_address_or_place_id($customer_id: bigint!, $address: String!, $place_id: String!) {
          locations(
            where: {
              customer_id: { _eq: $customer_id }
              _or: [{ address: { _ilike: $address } }, { place_id: { _eq: $place_id } }]
            }
          ) {
            ...Location
          }
        }
        ${_parent.fragments.simpleLocation()}
      `,
      { customer_id: customer_id, address: `${address}%`, place_id: place_id }
    ),

  /**
   * Query a location by address
   *
   * @param {bigint} customer_id - ID of the customer associated with the location
   * @param {String} address - the address of the location
   */
  getByAddress: (customer_id, address) =>
    _parent.gql.query(
      gql`
        query get_locations_by_address_or_place_id($customer_id: bigint!, $address: String!) {
          locations(
            where: {
              customer_id: { _eq: $customer_id }
              address: { _ilike: $address }
            }
          ) {
            ...Location
          }
        }
        ${_parent.fragments.simpleLocation()}
      `,
      { customer_id: customer_id, address: `${address}%`}
    ),

  /**
   * Returns either new new Location object from params and google map data
   *
   * @param  {Number} customer_id Idenifies customer location will be created for/by
   * @param  {String} name Name for new location (should be unique)
   * @param  {String} address Unique address with at least: <Street Number> <Street Name> <City> <State>
   * @param  {String} email (Optional) email to contact location's owner. Defaults to null.
   * @param  {String} phone (Optional) phone to contact location's owner. Defaults to null.
   * @param  {String} nickname (Optional) nickname for location. Defaults to null.
   * @param  {String} type type of location (defaults to "customer").
   * @returns {Object} Location Object
   *
   */
  async build(customer_id, name, address, email = null, phone = null, nickname = null, type = 'customer', options) {
    //Validate
    // if (customer_id === null || customer_id === undefined) throw Error('No customer_id entered');
    let addressIsValid = utilities.validateAddress(address);
    if (!addressIsValid) throw Error('Invalid address: ' + (address || 'no address entered'));
    if (name === null || name === undefined) {
      name = _parent.locations.defaultLocationName(address);
    }

    // Default values and direct inputs
    let location = {
      // tookan_id: null, (deprecated field)
      active: 1,
      customer_id: customer_id || null,
      name: name,
      address: address,
      email: email,
      phone: phone,
      nickname: nickname,
      type: type,
      updatedat: 'now()',
    };

    // Values from google maps API
    let noTimezoneCall = options ? options.noTimezoneCall : false;
    let mapsDataObj = await _parent.google.returnLocationData(address, noTimezoneCall);

    // combination of both
    let newLocation = Object.assign(location, mapsDataObj);
    let geopoint = [mapsDataObj.longitude, mapsDataObj.latitude];

    //Check if new location is missing address subfields- if so attempt to fill them in from the address returned from google
    if (!newLocation.address_line_one || !newLocation.city || !newLocation.state || !newLocation.zip_code) {
      const addressSubfieldsFromString = _parent.locations.parseAddressSubfields(newLocation, newLocation.address);
      newLocation = Object.assign(newLocation, addressSubfieldsFromString);
    }

    //Get Region
    let regionRes = await _parent.regions.getByCoords(geopoint);
    //if the location does not exist in any current region, we still create it
    let region_id = regionRes.data && regionRes.data.length > 0 ? regionRes.data[0].id : null;
    log && console.log('found region_id:', region_id);
    newLocation.region_id = region_id;
    log && console.log('New Location: ' + JSON.stringify(newLocation));
    return newLocation;
  },

  /**
   * Returns either new (full) Location object or existing (partial) Location object
   * This function will NOT insert the built location, unlike getOrCreate
   *
   * @param  {Number} customer_id Idenifies customer location will be created for/by
   * @param  {String} name Name for new location (should be unique)
   * @param  {String} address Unique address with at least: <Street Number> <Street Name> <City> <State>
   * @param  {String} email (Optional) email to contact location's owner. Defaults to null.
   * @param  {String} phone (Optional) phone to contact location's owner. Defaults to null.
   * @param  {String} nickname (Optional) nickname for location. Defaults to null.
   * @param  {String} type type of location (defaults to "customer").
   * @param  {Object} options configures whether the function will do certain tasks such as set the timezone of the location.
   * @returns {Object} Location Object
   *
   */

  async getOrBuild(
    customer_id,
    name,
    address,
    email = null,
    phone = null,
    nickname = null,
    type = 'customer',
    options
  ) {
    if (typeof name !== 'string' || typeof address !== 'string') {
      throw new Error(`Invalid name or address: name=${name} address=${address}`);
    } else if (typeof customer_id !== 'number') {
      throw new Error(`Invalid customer id: customer_id=${customer_id}`);
    }
    try {
      // this location only contains id, address, and name fields. Used to query/create Lanes.
      let getLocRes = await _parent.locations.getByAddress(customer_id, address);
      if (!getLocRes.data || getLocRes.data.length < 1) {
        log && console.log('BUILD');
        let newLocation = await _parent.locations.build(
          customer_id,
          name,
          address,
          email,
          phone,
          nickname,
          type,
          options
        );
        log && console.log('newLoc', newLocation);
        return newLocation;
      } else {
        log && console.log('FOUND', getLocRes.data[0]);
        let foundLocation = getLocRes.data[0];
        return foundLocation;
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  },
  /**
   * Returns either existing location or newly created location
   * This function will INSERT the new location, unlike getOrBuild
   *
   * @param  {Number} customer_id Idenifies customer location will be created for/by
   * @param  {String} name Name for new location (should be unique)
   * @param  {String} address Unique address with at least: <Street Number> <Street Name> <City> <State>
   * @param  {String} email (Optional) email to contact location's owner. Defaults to null.
   * @param  {String} phone (Optional) phone to contact location's owner. Defaults to null.
   * @param  {String} nickname (Optional) nickname for location. Defaults to null.
   * @param  {String} type type of location (defaults to "customer").
   * @returns {Object} Location Object
   *
   */

  async getOrCreate(
    customer_id,
    name,
    address,
    email = null,
    phone = null,
    nickname = null,
    type = 'customer',
    options
  ) {
    if (typeof name !== 'string' || typeof address !== 'string') {
      throw new Error(`Invalid name or address: name=${name} address=${address}`);
    } else if (typeof customer_id !== 'number') {
      throw new Error(`Invalid customer id: customer_id=${customer_id}`);
    }
    try {
      let location = await _parent.locations.getOrBuild(
        customer_id,
        name,
        address,
        email,
        phone,
        nickname,
        type,
        options
      );
      //If no id, location has not yet been inserted into db
      if (location.id === undefined) {
        //locations.create auto-sets active and updateAt
        delete location.active;
        delete location.updatedat;
        // We use an upsert here to avoid issues where the location imputs do not match any address in the database
        //   and the corrected address returned from google DOES match an address in our database.
        // If we do not do this, we often will get unique key constraint errors for attempting to make duplicate locations
        log && console.log('NewLocationReq', location);
        let newLocationRes = await _parent.locations.create(location);
        log && console.log('NewLocationRes', newLocationRes);
        if (newLocationRes.success && newLocationRes.data[0]) {
          return { success: true, data: newLocationRes.data[0] };
        } else {
          let message = newLocationRes.errors ? newLocationRes.errors[0].message : '';
          return { success: false, error: new Error('getOrCreate location: Error creating new location: ' + message) };
        }
      } else {
        return { success: true, data: location };
      }
    } catch (err) {
      console.error(err);
      return { success: false, error: err };
    }
  },

  /**
   * Creates a name for a location from an address
   *
   * @param  {String} address Location's address
   * @returns  {String} Name for new location (Street address of location)
   */
  defaultLocationName(address) {
    //Note: this assumes address is formatted correctly (with commas)
    let addressArr = address.split(',');
    //Use the street address as the name
    let locationName = addressArr[0].trim();
    return utilities.capEach(locationName);
  },

  /**
   * Create a valid address string from a series of params
   *
   * @param  {String} addressOne - The street name and number
   * @param  {String} city - city name
   * @param  {String} state - state name or postal abbr
   * @param  {String} zip - zip code (optional)
   * @param  {String} addressTwo - Apartment number, suite number, etc (optional)
   * @returns {String} - Formatted string
   *
   */
  buildAddress: function (addressOne, city, state, zip = '', addressTwo = '', fullAddress) {
    // Check for required address components
    if (!fullAddress && (!addressOne || !city || !state)) {
      throw new Error('Missing required address component');
    } else if (fullAddress) {
      log && console.log(`Full Address Used:`, fullAddress);
      return fullAddress;
    }

    // Create the array of address parts
    let baseAddressArr = [addressOne, city, state];
    if (addressTwo) baseAddressArr[0] = `${addressOne} ${addressTwo}`;
    if (zip) baseAddressArr[2] = `${state} ${zip}`;

    // Filter out falsey values
    let filteredAddressArr = baseAddressArr.filter(e => e);

    // Join a full address
    let composedAddress = filteredAddressArr.join(', ');
    log && console.log(`Composed Address Used:`, composedAddress);
    return composedAddress;
  },

  /**
   * Build a location response object out of location values
   * If location is undefined it will return defaults
   *
   * @param {Object} location - the location object returned from hasura
   * @param  {String} location.name - The location's name
   * @param  {String} location.address - the location address
   * @param  {Int} location.id - the location id
   * @param  {Numeric} location.latitude - the location latitude
   * @param  {Numeric} location.longitude - the location longitude
   * @param  {String} location.place_id - the location longitude
   * @returns {Object} - Object containing name, street address, city, state, zip and address details
   *
   */
  buildLocationResObj: function (
    location = {
      name: 'name',
      address: 'address, city, state zip',
      latitude: 0,
      longitude: 0,
      place_id: 'place_id',
      id: 0,
    }
  ) {
    const { name, address, latitude, longitude, id, place_id } = location;
    //Parse different sections of the address to return as separate props
    let addressArr = address ? address.split(', ') : [];
    let address_one = addressArr[0] || null;
    let city = addressArr[1] || null;
    //Zip and state are not separated by a comma and a space, so they have to be parsed separately
    let stateAndZip = addressArr[2] ? addressArr[2].split(' ') : null;
    let state = stateAndZip ? stateAndZip[0] : null;
    let zip = stateAndZip ? stateAndZip[1] : null;
    const locResObj = {
      name: name,
      address_one: address_one,
      city: city,
      state: state,
      zip: zip,
      lat: latitude,
      lon: longitude,
      google_place_id: place_id,
      location_id: id,
      full_address: address,
    };
    return locResObj;
  },

  /**
   * Parse location subfields from a full address string
   *
   * @param {Object} location - the location object, will populate subfields if they are present
   * @param  {String} address - the location address returned from google
   * @returns {Object} - Object containing address_line_one, city, state, zip
   *
   */
  parseAddressSubfields: function (
    location = { address_line_one: null, city: null, state: null, zip_code: null },
    address
  ) {
    const addressSubfieldsObj = {
      address_line_one: location.address_line_one || null,
      city: location.city || null,
      state: location.state || null,
      zip_code: location.zip_code || null,
    };
    if (!address) {
      return addressSubfieldsObj;
    }
    const addressArr = address.split(', ');
    const addressLineOne = addressArr[0];
    const city = addressArr[1];
    const stateAndZip = addressArr[2];
    //We do some validation on the state and zip (we can't really do this for address line one or city)
    const state = stateAndZip.split(' ')[0] && stateAndZip.split(' ')[0].length === 2 ? stateAndZip.split(' ')[0] : null;
    const zip = stateAndZip.split(' ')[1] && stateAndZip.split(' ')[1].length === 5 ? stateAndZip.split(' ')[1] : null;
    //Only fill in the subfields if they are not already filled in
    for(let key in addressSubfieldsObj){
      if(key === 'address_line_one' && !addressSubfieldsObj[key]){
        addressSubfieldsObj[key] = addressLineOne;
      }
      if(key === 'city' && !addressSubfieldsObj[key]){
        addressSubfieldsObj[key] = city;
      }
      if(key === 'state' && !addressSubfieldsObj[key]){
        addressSubfieldsObj[key] = state;
      }
      if(key === 'zip_code' && !addressSubfieldsObj[key]){
        addressSubfieldsObj[key] = zip;
      }
    }
    return addressSubfieldsObj;
  },
};

module.exports = locations;
